import { Fragment, useEffect, useRef, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { ChevronLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { PaperClipIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import { trackPromise } from "react-promise-tracker";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router";

import { getStrapiClient } from "@/lib/strapiClient";

import type { Accident } from "../../models/accident";
import type { RootState } from "../../redux/store";

export default function AccidentsDetails() {
	const { t, i18n } = useTranslation();
	const { accident_id } = useParams();
	const navigate = useNavigate();

	const [openEraseAlert, setOpenEraseAlert] = useState(false);
	const userState = useSelector((state: RootState) => state.user);

	const cancelButtonRef = useRef(null);
	const [accident, setAccident] = useState<Accident>();
	const Skeleton = ({ className }: { className?: string }) => (
		<div className={clsx("animate-pulse rounded-md bg-gray-200", className)} />
	);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const client = getStrapiClient();
				const response = await client.collection("accidents").findOne(accident_id ?? "", {
					populate: [
						"coordinates",
						"country",
						"region",
						"massi",
						"scope",
						"temporada",
						"orientation",
						"progressio",
						"accuracy",
						"accuracy.units",
						"cota",
						"origen",
						"snow_type",
						"danger_degree",
						"desencadenant",
						"activitat",
						"material",
						"doc",
						"gallery",
					],
				});
				setAccident(response.data as Accident);
			} catch (error) {
				console.error(error);
				navigate("/");
			}
		};

		trackPromise(fetchData());
	}, [accident_id, navigate]);

	const deleteAccident = async ({ id }: { id: string }) => {
		try {
			const client = getStrapiClient();
			await client.collection("accidents").delete(id);
			navigate("/");
		} catch (error) {
			console.error(error);
		}
	};

	if (!accident) {
		return (
			<div className="space-y-5 p-4">
				<div className="flex items-center space-x-3">
					<Skeleton className="h-8 w-8 rounded-full" />
					<Skeleton className="h-6 w-48" />
				</div>
				<div className="h-64 w-full overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
					<Skeleton className="h-full w-full" />
				</div>
				{Array.from({ length: 3 }).map((_, idx) => (
					<div key={idx} className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
						<Skeleton className="mb-3 h-5 w-32" />
						<div className="grid grid-cols-2 gap-3">
							{Array.from({ length: 4 }).map((__, subIdx) => (
								<div key={subIdx} className="space-y-2">
									<Skeleton className="h-3 w-24" />
									<Skeleton className="h-4 w-32" />
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<>
			<div className="p-4">
				<div className="flex items-center space-x-4 py-4">
					<button
						type="button"
						onClick={() => navigate("/")}
						className="rounded-full border border-blue-500 p-2 text-blue-500 hover:bg-blue-500 hover:text-white focus:ring-3 focus:ring-blue-900"
					>
						<ChevronLeftIcon className="h-5 w-5" />
					</button>
					<div className="font-medium text-gray-900 text-xl">{t("accidents.details.title")}</div>
				</div>
				<div className="space-y-5">
					{accident.gallery && accident.gallery.length > 0 && (
						<div className="h-1/4 w-full place-content-center">
							<a href={accident.gallery[0].url}>
								<img
									src={accident.gallery[0].url}
									alt={accident.gallery[0].name}
									className="h-96 w-full rounded-sm object-cover"
								/>
							</a>
						</div>
					)}
					<div className="overflow-hidden bg-white shadow-sm sm:rounded-lg">
						<div className="px-4 py-5 sm:px-6">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.details.location")}</h3>
						</div>
						<div className="border-gray-200 border-t px-4 py-5 sm:px-6">
							<dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
								{accident.date && (
									<div className="sm:col-span-1">
										<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.date")}</dt>
										<dd className="mt-1 text-gray-900 text-sm">{moment(accident.date).format("DD/MM/YYYY")}</dd>
									</div>
								)}
								{accident.temporada && (
									<div className="sm:col-span-1">
										<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.season")}</dt>
										<dd className="mt-1 text-gray-900 text-sm">{accident.temporada.name}</dd>
									</div>
								)}
								<div className="sm:col-span-2">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.scope")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident.scope?.name_es ?? accident.scope?.name ?? "-")
											: (accident?.scope?.name ?? "-")}
									</dd>
								</div>
							</dl>
						</div>
					</div>
					<div className="overflow-hidden bg-white shadow-sm sm:rounded-lg">
						<div className="px-4 py-5 sm:px-6">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.details.location")}</h3>
						</div>
						<div className="border-gray-200 border-t px-4 py-5 sm:px-6">
							<dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.place")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.place ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.country")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.country?.name_es ?? accident?.country?.name ?? "-")
											: (accident?.country?.name ?? "-")}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.region")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.region?.name_es ?? accident?.region?.name ?? "-")
											: (accident?.region?.name ?? "-")}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.massis")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.massi?.name_es ?? accident?.massi?.name ?? "-")
											: (accident?.massi?.name ?? "-")}
									</dd>
								</div>

								{accident.coordinates && (
									<div className="sm:col-span-2">
										<dt className="font-medium text-gray-500 text-sm">{t("accidents.details.localization")}</dt>
										<dd className="mt-1 h-60">
											<MapContainer
												center={
													accident
														? [accident.coordinates.latitude, accident.coordinates.longitude]
														: [43.003069514915786, -0.9930763932215235]
												}
												zoom={10}
												scrollWheelZoom={false}
												style={{ height: "100%", width: "100%" }}
											>
												<TileLayer
													attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
													url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
												/>
												<Marker
													key={`marker-${accident.coordinates.latitude}`}
													position={[accident.coordinates.latitude, accident.coordinates.longitude]}
												/>
											</MapContainer>
										</dd>
									</div>
								)}
								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.coordinates.lat")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.coordinates.latitude}</dd>
								</div>
								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.coordinates.lng")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.coordinates.longitude}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.orientation")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident?.orientation?.name ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.progressio")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{(i18n.language === "es"
											? (accident?.progressio?.name_es ?? accident?.progressio?.name ?? "-")
											: accident?.progressio?.name) ?? "-"}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.accuracy")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.accuracy?.name_es ?? accident?.accuracy?.name ?? "-")
											: (accident?.accuracy?.name ?? "-")}{" "}
										({accident?.accuracy?.units?.number ?? "-"} {accident?.accuracy?.units?.units ?? "-"})
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.cota")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.cota?.name_es ?? accident?.cota?.name ?? "-")
											: (accident?.cota?.name ?? "-")}
									</dd>
								</div>
							</dl>
						</div>
					</div>
					<div className="overflow-hidden bg-white shadow-sm sm:rounded-lg">
						<div className="px-4 py-5 sm:px-6">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.details.data")}</h3>
						</div>
						<div className="border-gray-200 border-t px-4 py-5 sm:px-6">
							<dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.origen")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.origen?.name_es ?? accident?.origen?.name ?? "-")
											: (accident?.origen?.name ?? "-")}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.snow_type")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.snow_type?.name_es ?? accident?.snow_type?.name ?? "-")
											: (accident?.snow_type?.name ?? "-")}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.size")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.size ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.danger_degree")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.danger_degree?.name_es ?? accident?.danger_degree?.name ?? "-")
											: (accident?.danger_degree?.name ?? "-")}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.desencadenant")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.desencadenant?.name_es ?? accident?.desencadenant?.name ?? "-")
											: (accident?.desencadenant?.name ?? "-")}{" "}
										({accident?.desencadenant?.code ?? "-"})
									</dd>
								</div>
							</dl>
						</div>
					</div>
					<div className="overflow-hidden bg-white shadow-sm sm:rounded-lg">
						<div className="px-4 py-5 sm:px-6">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.details.act_damages")}</h3>
						</div>
						<div className="border-gray-200 border-t px-4 py-5 sm:px-6">
							<dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.activitat")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.activitat?.name_es ?? accident?.activitat?.name ?? "-")
											: (accident?.activitat?.name ?? "-")}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.material")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">
										{i18n.language === "es"
											? (accident?.material?.name_es ?? accident?.material?.name ?? "-")
											: (accident?.material?.name ?? "-")}
									</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.damage")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.damage ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.members")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.members ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.triggers")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.triggers ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.dragged")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.dragged ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.damaged")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.damaged ?? "-"}</dd>
								</div>

								<div className="sm:col-span-1">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.dead")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.dead ?? "-"}</dd>
								</div>
							</dl>
						</div>
					</div>
					{((userState.loggedIn && accident.doc && accident.doc.length > 0) || accident.link) && (
						<div className="overflow-hidden bg-white shadow-sm sm:rounded-lg">
							<div className="px-4 py-5 sm:px-6">
								<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.details.multimedia")}</h3>
							</div>
							<div className="border-gray-200 border-t px-4 py-5 sm:px-6">
								<dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
									{userState.loggedIn && accident.doc && accident.doc.length > 0 && (
										<div className="sm:col-span-2">
											<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.docs")}</dt>
											{accident.doc.map((doc) => (
												<dd key={doc.id} className="mt-1 text-gray-900 text-sm">
													<div className="divide-y divide-gray-200 rounded-md border border-gray-200">
														<div className="flex items-center justify-between py-3 pr-4 pl-3 text-sm">
															<div className="flex w-0 flex-1 items-center">
																<PaperClipIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
																<span className="ml-2 w-0 flex-1 truncate">{doc.name}</span>
															</div>
															<div className="ml-4 shrink-0">
																<a href={doc.url} className="font-medium text-blue-600 hover:text-blue-500">
																	{t("accidents.details.download")}
																</a>
															</div>
														</div>
													</div>
												</dd>
											))}
										</div>
									)}
									{accident.link && (
										<div className="sm:col-span-2">
											<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.link")}</dt>
											<dd className="mt-1 text-gray-900 text-sm">
												<a href={accident.link} className="hover:text-blue-800 hover:underline">
													{accident.link}
												</a>
											</dd>
										</div>
									)}
								</dl>
							</div>
						</div>
					)}
					<div className="overflow-hidden bg-white shadow-sm sm:rounded-lg">
						<div className="px-4 py-5 sm:px-6">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.details.other")}</h3>
						</div>
						<div className="border-gray-200 border-t px-4 py-5 sm:px-6">
							<dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
								<div className="sm:col-span-2">
									<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.observations")}</dt>
									<dd className="mt-1 text-gray-900 text-sm">{accident.observations}</dd>
								</div>
							</dl>
						</div>
					</div>
				</div>
				{userState.loggedIn && (
					<div className="mt-5 flex justify-end">
						<Link
							to="edit"
							className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
						>
							{t("accidents.details.buttons.edit")}
						</Link>
						<button
							type="button"
							className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
							onClick={() => setOpenEraseAlert(true)}
						>
							{t("accidents.details.buttons.delete")}
						</button>
					</div>
				)}
			</div>
			<Transition.Root show={openEraseAlert} as={Fragment}>
				<Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => setOpenEraseAlert(false)}>
					<div className="relative flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<div className="fixed inset-0 z-40 bg-black/30 transition-opacity" aria-hidden="true" />
						</Transition.Child>

						{/* This element is to trick the browser into centering the modal contents. */}
						<span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
							&#8203;
						</span>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div className="relative z-50 inline-block w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-5 text-left align-middle shadow-xl transition-all sm:my-8 sm:p-6">
								<div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6">
									<div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-50 sm:mx-0">
										<ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
									</div>
									<div className="flex flex-col gap-4">
										<Dialog.Title as="h3" className="font-semibold text-xl text-gray-900">
											{t("accidents.details.delete.title")}
										</Dialog.Title>
										<p className="max-w-xl text-base leading-6 text-gray-600">{t("accidents.details.delete.text")}</p>
										<div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
											<button
												type="button"
												className="inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
												onClick={() => setOpenEraseAlert(false)}
												ref={cancelButtonRef}
											>
												{t("accidents.details.delete.cancel")}
											</button>
											<button
												type="button"
												className="inline-flex justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
												onClick={() => {
													setOpenEraseAlert(false);
													trackPromise(deleteAccident({ id: `${accident.documentId ?? accident.id}` }));
												}}
											>
												{t("accidents.details.delete.button")}
											</button>
										</div>
									</div>
								</div>
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition.Root>
		</>
	);
}
